import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { DocumentTypeDatabase } from '../../../../types';
import { useCreateDocumentType } from '../../api/useCreateDocumentType';
import { useUpdateDocumentType } from '../../api/useUpdateDocumentType';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Le nom est obligatoire'),
});

type FormValues = z.input<typeof formSchema>;
type FormValuesOutput = z.infer<typeof formSchema>;

export type Props = {
  spaceId: string;
  typeToEdit?: DocumentTypeDatabase | null;
  onClose: () => void;
  onSuccess?: (type: DocumentTypeDatabase) => void;
};

export default function useCreateEditDocumentTypeModal(props: Props) {
  const createType = useCreateDocumentType();
  const updateType = useUpdateDocumentType();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<FormValues, unknown, FormValuesOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: props.typeToEdit?.name ?? '',
    },
  });

  async function handleSubmit(data: FormValuesOutput) {
    setServerError(null);

    try {
      if (props.typeToEdit) {
        const updated = await updateType.mutateAsync({
          id: props.typeToEdit.id,
          name: data.name,
        });
        props.onSuccess?.(updated);
      } else {
        const created = await createType.mutateAsync({
          spaceId: props.spaceId,
          name: data.name,
        });
        props.onSuccess?.(created);
      }
      props.onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Une erreur est survenue');
    }
  }

  const isSubmitting = createType.isPending || updateType.isPending;

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting,
    serverError,
    isEdit: !!props.typeToEdit,
  };
}
